import { useMemo } from "react";
import { difference, orderBy } from "lodash";
import { isNullish } from "../lib/util/common-util";

export function useKeyedData<V extends { key: number }, CF extends V>(
  value: V[],
  changedFrom?: CF[],
) {
  const valueKeys = useMemo(() => value.map((v) => v.key), [value]);
  const changedFromKeys = useMemo(
    () => changedFrom?.map((v) => v.key) ?? [],
    [changedFrom],
  );

  const addedKeys = useMemo(
    () => difference(valueKeys, changedFromKeys),
    [changedFromKeys, valueKeys],
  );
  const deletedKeys = useMemo(
    () => difference(changedFromKeys, valueKeys),
    [changedFromKeys, valueKeys],
  );

  /**
   * 削除済のものも含めた、キーつきの値。
   */
  const keyedValues: {
    mode: "none" | "added" | "deleted";
    value: V;
  }[] = useMemo(() => {
    if (isNullish(changedFrom))
      return value.map((v) => ({ mode: "none", value: v }));
    const _value = value.map((v) => ({
      mode: addedKeys.includes(v.key) ? ("added" as const) : ("none" as const),
      value: v,
    }));
    const _deleted = changedFrom
      .filter((v) => deletedKeys.includes(v.key))
      .map((v) => ({
        mode: "deleted" as const,
        value: v,
      }));
    return orderBy([..._value, ..._deleted], [(v) => v.value.key]);
  }, [changedFrom, value, addedKeys, deletedKeys]);

  return {
    valueKeys,
    changedFromKeys,
    addedKeys,
    deletedKeys,
    keyedValues,
  };
}
